import React from "react";
import capitalize from "lodash/capitalize";
import startCase from "lodash/startCase";
import Link from "next/link";
import { useParams } from "next/navigation";

import { getDeals } from "@/api/requests/getDeals";
import { getPromotions } from "@/api/requests/getPromotions";
import { CATEGORY_NAMES } from "@/constants/dispensary";
import { Action, Category } from "@/constants/events";
import { Coordinates } from "@/custom-types/Coordinates";
import { Deal } from "@/custom-types/Deals";
import { Location } from "@/custom-types/Location";
import { Promotion } from "@/custom-types/Promotions";
import { useEventTracker } from "@/hooks/useEventTracker";
import { coinListDataFactory } from "@/utils/coinListDataFactory";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import { geolocation } from "@/utils/finder/contextData/getFinderLocation";
import { retrieveLocationUrl } from "@/utils/locationsUtils";
import logError from "@/utils/logError";

import Breadcrumb from "@/components/Breadcrumb";
import Breadcrumbs from "@/components/Breadcrumbs";
import CoinList from "@/components/CoinList";
import DealsDisclaimer from "@/components/DealsDisclaimer";
import DealsHero from "@/components/DealsHero";
import DealsResults from "@/components/DealsResults";
import MetaTags from "@/components/MetaTags";
import PhenotypeCarousel from "@/components/PhenotypeCarousel";
import PromoResults from "@/components/PromoResults";
import PromotedStrainCarousel from "@/components/StrainCarousel";

const DEALS_PER_PAGE = 6;

const extractLocation = (slug: string) => {
  const slugArr = slug.split("-");

  // length of 3 is city-state-country
  if (slugArr.length >= 3) {
    const state = slugArr[slugArr.length - 2].toUpperCase();
    const city = slugArr
      .slice(0, -2)
      .map((word: string) => capitalize(word))
      .join(" ");

    return { city, state };
  }

  // length of 2 is city-state or state-country
  if (slugArr.length === 2) {
    return { city: startCase(slugArr[0]), state: slugArr[1].toUpperCase() };
  }
};

/** if country is missing in slug, append it to the
 *  end to use in SEO canonical URL */
const formatSlug = (slug: string, countryCode: string) => {
  const countryCodes = ["us", "ca"];
  const slugArr = slug.split("-");

  const lastIndex = slugArr[slugArr.length - 1];
  if (lastIndex && !countryCodes.includes(lastIndex)) {
    slugArr.push(countryCode);
    return slugArr.join("-");
  }

  return slug;
};

type DealsLocationPageProps = {
  coordinates: Coordinates;
  countryCode: string;
  deals: { deals: Deal[]; totalCount: number; dealCategories: string[] };
  locationTitle: string;
  promotions: Promotion[];
};

const DealsLocationPage: React.FC<DealsLocationPageProps> = ({
  coordinates,
  countryCode,
  deals,
  locationTitle,
  promotions,
}) => {
  const params = useParams();
  const slug = params?.slug;
  const { publishEvent } = useEventTracker();

  const isCanada = countryCode === "CA";

  const pageDescription = isCanada
    ? `Looking for weed deals ${locationTitle && `in ${locationTitle}`}? Find special sales, promo codes, coupons, and discounts on flower, carts, edibles, and dabs. Get the best dispensaries deals in your neighborhood on Leafly.`
    : `Looking for weed deals and discounts ${locationTitle && `in ${locationTitle}`}? Find special sales, promo codes, coupons, and discounts on flower, carts, edibles, and dabs. Get the best dispensaries deals in your neighborhood on Leafly`;

  const coinCategories = Object.keys(CATEGORY_NAMES).filter((category) =>
    deals.dealCategories.includes(category),
  );

  const coinListData = coinListDataFactory.createCoinListDataForCategories(
    coinCategories,
    CATEGORY_NAMES,
  );

  return (
    <>
      <MetaTags
        title={
          isCanada
            ? `${locationTitle} Licensed Dispensary Deals Near Me | Leafly`
            : `${locationTitle} Dispensary Weed Deals & Discounts Near Me`
        }
        description={pageDescription}
        canonicalOverride={`/deals/${formatSlug(String(slug), countryCode)}`}
      />

      <div className="container mt-xl mb-xxl">
        <h1 data-testid="deals-header" className="mb-lg heading--l">
          {isCanada
            ? `Licensed dispensary deals in ${locationTitle} near me`
            : `Dispensary deals in ${locationTitle}`}
        </h1>

        <DealsHero className="mb-section" />

        {deals.totalCount > 0 && (
          <>
            <h2 className="heading--m mb-md"> Weed deals by products </h2>
            <CoinList
              className="mb-section"
              coinData={coinListData}
              hrefLink={"/search/shop"}
              hrefLinkQueryParam={"&deal_active=true"}
              hrefProductCatQueryKey={"product_category"}
            />

            <h2 className="heading--m mb-md">Cannabis deals by strain type</h2>
            <PhenotypeCarousel className="mb-section" asLink linkToDeals />

            <PromotedStrainCarousel
              asLink
              className="mb-section"
              onClick={(strain) =>
                publishEvent({
                  action: Action.click,
                  category: Category.dealMenuStrains,
                  label: `strain name - ${strain.strainName}`,
                })
              }
              title="Deals on top weed strains near you"
            />
          </>
        )}

        <DealsResults
          className="mb-section"
          coordinates={coordinates}
          initialDeals={deals.deals}
          totalCount={deals.totalCount}
        />

        <PromoResults className="mb-section" promotions={promotions} />

        <Link className="underline mb-section block" href="/deals/locations">
          See all locations
        </Link>

        <DealsDisclaimer />
      </div>
      <Breadcrumbs className="mt-xl">
        <Breadcrumb href="/deals">Deals</Breadcrumb>
        <Breadcrumb currentLocation href={`/deals/${slug}`}>
          {locationTitle}
        </Breadcrumb>
      </Breadcrumbs>
    </>
  );
};

export const getServerSideProps =
  createGetServerSideProps<DealsLocationPageProps>(
    async ({ query, resolvedUrl }) => {
      const locationSlug = query.slug as string;

      if (
        locationSlug !== locationSlug.toLowerCase() ||
        locationSlug.split("-").length < 3
      ) {
        return {
          notFound: true,
        };
      }

      const location = extractLocation(locationSlug);

      if (!location) {
        return {
          notFound: true,
        };
      }

      const { city, coordinates, countryCode, geoSlug, state } =
        (await geolocation(locationSlug).catch(() => {
          logError(
            `Failed to determine coordinates for ${locationSlug} in /deals/[slug] getServersideProps`,
          );

          return {};
        })) as Location & {
          coordinates: { lat: number; lon: number };
        };

      if (!coordinates || !countryCode || locationSlug !== geoSlug) {
        return {
          notFound: true,
        };
      }

      const countryString = countryCode === "US" ? "united states" : "canada";
      const locationUrl = retrieveLocationUrl({
        city,
        countryString,
        state,
      });

      if (locationUrl && resolvedUrl !== locationUrl) {
        return {
          redirect: {
            destination: locationUrl,
            statusCode: 301,
          },
        };
      }

      const [deals, { promotions }] = await Promise.all([
        getDeals(coordinates, countryCode, 0, DEALS_PER_PAGE),
        getPromotions(coordinates, countryCode),
      ]);

      return {
        props: {
          coordinates,
          countryCode,
          deals,
          locationTitle: `${location.city}, ${location.state}`,
          promotions,
        },
      };
    },
  );

export default DealsLocationPage;
